import { put, call, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import clone from 'ramda/src/clone';
import filter from 'ramda/src/filter';
import find from 'ramda/src/find';
import findIndex from 'ramda/src/findIndex';
import isEmpty from 'ramda/src/isEmpty';
import pickBy from 'ramda/src/pickBy';
import propEq from 'ramda/src/propEq';
import AppActions from '~/actions/app';
import CompanyActions from '~/actions/company';
import CommunityActions from '~/actions/community';
import VendorActions from '~/actions/vendor';
import { history } from '~/reducers';
import { InitialGlobalSearchResult } from '~/services/config';
import { isNotNull } from '~/services/util';
import decamelize from '~/services/decamelize';
import NavigationService from '~/services/NavigationService';

import xmljs from 'xml-js';

// fetch vendor categories
export function* getcategoriesRequest(api, action) {
    try {
        const isCategory = action?.payload?.isCategory;
        const after = action.payload?.after;
        const response = yield api.getVendorCategories();

        if (response.ok) {
            yield put(VendorActions.getcategoriesSuccess(response.data));
            if (!isCategory) {
                yield put(VendorActions.getallcategorypopularproductsRequest());
            }
            if (after) yield put({ ...after });
        } else {
            yield put(VendorActions.getcategoriesFailure());
        }
    } catch (e) {
        yield put(VendorActions.getcategoriesFailure());
    }
}

export function* getcategorypopularproductsRequest(api, action) {
    try {
        const { id } = action;
        const response = yield api.getCategoryPopularProducts(id);

        if (response.ok) {
            yield put(VendorActions.getcategorypopularproductsSuccess({ categoryId: id, data: response.data }));
        } else {
            yield put(VendorActions.getcategorypopularproductsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getcategorypopularproductsFailure());
    }
}

export function* getallcategorypopularproductsRequest(api, action) {
    try {
        const response = yield api.getAllCategoryPopularProducts();

        if (response.ok) {
            const { data } = response;
            const categories = Object.keys(data).map((key) => ({
                id: data[key].category.id,
                name: key,
                products: data[key].products,
                product_total: data[key].product_total,
                total_verified_ratings: data[key].total_verified_ratings,
            }));
            yield put(VendorActions.getallcategorypopularproductsSuccess(categories));
        } else {
            yield put(VendorActions.getallcategorypopularproductsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getallcategorypopularproductsFailure());
    }
}

export function* getcategoryproductsRequest(api, action) {
    try {
        const { categoryId, payload, after, route, callback } = action.payload;
        const options = queryString.stringify(payload);

        const response = yield api.getCategoryProducts(categoryId, options);
        if (response.ok) {
            yield put(VendorActions.getcategoryproductsSuccess({ categoryId, data: response.data }));
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback(response.data);
        } else {
            yield put(VendorActions.getcategoryproductsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getcategoryproductsFailure());
    }
}

/**
 * Get company detail
 * @param {*} payload { companyId, businessId, page, perPage, route, after }
 */
export function* getvendorcompanyRequest(api, action) {
    try {
        const { companyId, businessId, type, page, perPage, route, after } = action.payload;
        const token = yield select((state) => state.app.token);

        let options = businessId ? `id=${businessId}` : '';
        options = page ? `${options}&page=${page}` : options;
        options = perPage ? `${options}&per_page=${perPage}` : options;

        const response = type === 'hcm' ? yield api.getCompany(companyId) : token ? yield api.getVRCompany(companyId) : yield api.getGetCompany(companyId);
        const resBusiness = yield api.getCompanyBusiness(options);
        const resBlogs = yield api.getBlogPosts(`company_id=${companyId}`);
        const resBlog = token ? yield api.getGetBlog('company', companyId) : {};

        if (response.ok) {
            let company = {};
            if (type === 'hcm') {
                company = {
                    ...response.data,
                    business: resBusiness.ok ? resBusiness.data : {},
                    blogs: resBlogs.data?.posts?.data || [],
                    blog: resBlog.ok ? resBlog.data : {},
                };
            } else {
                company = {
                    ...response.data.company,
                    admins: response.data.admins,
                    business: resBusiness.ok ? resBusiness.data : {},
                    blogs: resBlogs.data?.posts?.data || [],
                    blog: resBlog.ok ? resBlog.data : {},
                };
            }
            yield put(VendorActions.getvendorcompanySuccess(company));
            if (route && !isEmpty(route)) yield call(NavigationService.push, route);
            if (after) yield put({ ...after });
        } else {
            yield put(VendorActions.getvendorcompanyFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvendorcompanyFailure());
    }
}

export function* getcompanyadminsRequest(api, action) {
    try {
        const { companyId, type } = action.payload;

        if (type === 'vr') {
            const response = yield api.getVRCompany(companyId);
            if (response.ok) {
                const { admins } = response.data;
                yield put(VendorActions.getcompanyadminsSuccess({ companyId, admins }));
            } else {
                yield put(VendorActions.getcompanyadminsFailure());
            }
        } else if (type === 'hcm') {
            const response = yield api.getCompanyUsers(companyId, 5000);
            if (response.ok) {
                const admins = filter(propEq('app_role_id', 2), response.data);
                yield put(VendorActions.getcompanyadminsSuccess({ companyId, admins }));
            } else {
                yield put(VendorActions.getcompanyadminsFailure());
            }
        }
    } catch (e) {
        yield put(VendorActions.getcompanyadminsFailure());
    }
}

/**
 * Get vendor companies
 * @param {*} payload { sort, letter, search }
 * @param {string} sort
 * @param {string} letter
 * @param {*} query { letter, search, page, per_page }
 */
export function* getvendorcompaniesRequest(api, action) {
    try {
        const { sort, payload, route, after, callback } = action.payload;
        const queryOptions = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const options = sort ? `${sort}?${queryOptions}` : queryOptions;

        const res = sort === 'alphabet' ? yield api.getVendorCompaniesAlphabet(options) : yield api.getVendorCompanies(options);
        if (res.ok) {
            yield put(VendorActions.getvendorcompaniesSuccess({ sort, data: res.data }));
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
        } else {
            yield put(VendorActions.getvendorcompaniesFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvendorcompaniesFailure());
    }
}

/**
 * @description Gets single VR company by id with all related products
 * @param {number} companyId vendor company id
 */
export function* getvrcompanydetailRequest(api, action) {
    try {
        const { companyId, callback, route, after } = action.payload;
        const token = yield select((state) => state.app.token);

        const response = token ? yield api.getVRCompany(companyId) : yield api.getGetCompany(companyId);
        if (response.ok) {
            yield put(VendorActions.getvrcompanydetailSuccess(response.data));
            if (callback) yield callback(response.data);
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
        } else {
            yield put(VendorActions.getvrcompanydetailFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvrcompanydetailFailure());
    }
}

export function* getvendorcategoriesRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload));
        const response = yield api.getSuperAdminCategories(options);

        if (response.ok) {
            yield put(VendorActions.getvendorcategoriesSuccess(response.data?.categories));
        } else {
            yield put(VendorActions.getvendorcategoriesFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvendorcategoriesFailure());
    }
}

export function* getvendorratingsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload));
        const response = yield api.getVendorRatings(options);

        if (response.ok) {
            yield put(VendorActions.getvendorratingsSuccess(response.data?.ratings));
        } else {
            yield put(VendorActions.getvendorratingsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvendorratingsFailure());
    }
}

/**
 * Get the simple info of all the vendor companies
 */
export function* getallvendorcompaniesRequest(api, action) {
    try {
        const { route, after, callback } = action.payload;
        const res = yield api.getAllVendorCompanies();
        if (res.ok) {
            yield put(VendorActions.getallvendorcompaniesSuccess(res.data));
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
        } else {
            yield put(VendorActions.getallvendorcompaniesFailure());
        }
    } catch (e) {
        yield put(VendorActions.getallvendorcompaniesFailure());
    }
}

/**
 * Save Vendor Rating
 * @param {*} payload { product_id, summary, pros, cons, phone, likely, star_rating, product_name }
 */
export function* savevendorratingRequest(api, action) {
    try {
        const response = yield api.postSaveVendorRating(action.payload);
        if (response.ok) {
            toast.success(`Thanks for rating ${action.payload.product_name}. Once verified we will publish it. Please expect a call from our verification team.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.savevendorratingSuccess());
            yield put(AppActions.modalRequest({ type: '' }));
        } else {
            toast.error(`Oops, Your action cannot be completed!. Please try later`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.savevendorratingFailure());
        }
    } catch (e) {
        yield put(VendorActions.savevendorratingFailure());
    }
}

export function* getvendorawardsRequest(api, action) {
    try {
        const response = yield api.getVendorAwards();

        if (response.ok) {
            yield put(VendorActions.getvendorawardsSuccess(response.data));
        } else {
            yield put(VendorActions.getvendorawardsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvendorawardsFailure());
    }
}

export function* getrecentlyupdatedproductsRequest(api, action) {
    const { payload } = action;
    try {
        const response = yield api.getRecentlyUpdatedProducts(queryString.stringify(payload));

        if (response.ok) {
            yield put(VendorActions.getrecentlyupdatedproductsSuccess(response.data));
        } else {
            yield put(VendorActions.getrecentlyupdatedproductsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getrecentlyupdatedproductsFailure());
    }
}

export function* getpopularproductsRequest(api, action) {
    const response = yield api.getPopularProducts();

    if (response.ok) {
        yield put(VendorActions.getpopularproductsSuccess(response.data));
    } else {
        yield put(VendorActions.getpopularproductsFailure());
    }
}

/**
 * Global Search
 * @param {*} payload { search: "drive", how_to_search: "exact" }
 * @param {*} type one of all, people, companies, products, blogs, forums
 * @param {*} route
 * @param {*} after
 */
export function* globalsearchRequest(api, action) {
    try {
        const { payload, after, route, callback, target } = action;
        const options = queryString.stringify(payload, { arrayFormat: 'bracket' });
        if (!payload.search || payload.search === '') {
            yield put(VendorActions.globalsearchSuccess({ target, search_text: '', data: InitialGlobalSearchResult }));
        } else {
            let response = {};
            if (target === 'blogs') {
                const res = yield api.getBlogPosts(options);
                response = { ok: true, data: res.data.posts };
            } else if (target === 'forums') {
                const res = yield api.getGetTopics(options);
                response = { ok: true, data: res.data.topics };
            } else {
                const resVRPro = yield api.getVRProducts(options);
                const res = yield api.postGlobalSearch(options);
                response = {
                    ok: true,
                    data: {
                        ...res.data,
                        blogs: { data: res.data.blogs },
                        companies: { data: res.data.businesses },
                        forums: { data: res.data.topics },
                        products: { data: [...res.data.products, ...resVRPro.data.data] },
                    },
                };
            }
            if (response.ok) {
                yield put(VendorActions.globalsearchSuccess({ target, search_text: payload.search, data: response.data }));
                if (after) yield put({ ...after });
                if (callback) yield callback(response.data);
                if (route) yield call(NavigationService.push, route);
            } else {
                yield put(VendorActions.globalsearchFailure());
            }
        }
    } catch (e) {
        yield put(VendorActions.globalsearchFailure());
    }
}

export function* savevrproductRequest(api, action) {
    const { data, companyData, route, after } = action.payload;
    try {
        let parent = {};
        if (companyData) {
            const compRes = yield api.postSaveVRCompany(companyData);
            if (compRes.ok) {
                parent = compRes.data.company;
            } else {
                toast.error(`Sorry, the company cannot be saved!. Please try later`, {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                yield put(VendorActions.savevrproductFailure());
                return;
            }
        }

        const productData = { product: { ...data.product, parent_id: parent.id } };
        const response = yield api.postSaveProduct(companyData ? productData : data);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            yield put(VendorActions.savevrproductSuccess(response.data));
        } else {
            toast.error(`${response.data.message}`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.savevrproductFailure());
        }
    } catch (e) {
        yield put(VendorActions.savevrproductFailure());
    }
}

export function* savevrcompanyRequest(api, action) {
    try {
        const { payload, callback } = action;
        const { data, image, type, route, after } = payload;
        if (image) {
            const resImage = yield api.postSaveVRPhoto(image);
        }
        const response = type === 'hcm' ? yield api.postBusinessCompany(data) : yield api.postSaveVRCompany(data);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            yield put(VendorActions.savevrcompanySuccess(response.data.company));
            if (callback) {
                yield callback(response.data.company);
            } else {
                toast.success(`Company Information Updated`, {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
            }
        } else {
            toast.error(`Sorry ${response.data.message}`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.savevrcompanyFailure());
        }
    } catch (e) {
        yield put(VendorActions.savevrcompanyFailure());
    }
}

export function* savevrphotoRequest(api, action) {
    try {
        const { data, callback, route, after } = action.payload;
        const response = yield api.postSaveVRPhoto(data);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback(response.data.path);
            yield put(VendorActions.savevrphotoSuccess());

            const avatar = response.data.return[response.data.return.length - 1];
            const blogs = yield select((state) => state.community.communityBlogs);
            const blog = clone(find((e) => e.entity.id === data.entity_id || e.user_id === data.user_id, blogs));
            if (blog) {
                blog.entity.user.avatar = avatar;
                yield put(CommunityActions.getcommunityblogSuccess(blog));
            }
        } else {
            yield put(VendorActions.savevrphotoFailure());
        }
    } catch (e) {
        yield put(VendorActions.savevrphotoFailure());
    }
}

export function* getvrcompanyRequest(api, action) {
    try {
        const { companyId, payload } = action;
        const options = queryString.stringify(payload);
        const response = yield api.getCompanyStats(companyId, options);
        if (response.ok) {
            yield put(VendorActions.getvrcompanySuccess({ ...response.data.company, admins: response.data.admins }));
        } else {
            yield put(VendorActions.getvrcompanyFailure());
        }
    } catch (e) {
        yield put(VendorActions.getvrcompanyFailure());
    }
}

export function* postvrcompanyadminRequest(api, action) {
    try {
        const { mode, data, after } = action.payload;
        const response = yield api.postVRCompanyAdmin(mode, data);
        if (response.ok) {
            if (after) yield put({ ...after });
            toast.success(`Successfully ${mode === 'add' ? 'added' : 'removed from'} the admins.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postvrcompanyadminSuccess());
        } else {
            toast.error(`Add admin failed.\n Please try it later!`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postvrcompanyadminFailure());
        }
    } catch (e) {
        yield put(VendorActions.postvrcompanyadminFailure());
    }
}

export function* postblogadminRequest(api, action) {
    try {
        const { mode, data, after } = action.payload;
        const response = yield api.postBlogCompanyAdmin(mode, data);
        if (response.ok) {
            if (after) yield put({ ...after });
            toast.success(`Successfully ${mode === 'add' ? 'added' : 'removed from'} the admins.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postblogadminSuccess());
        } else {
            toast.error(`Add admin failed.\n Please try it later!`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postblogadminFailure());
        }
    } catch (e) {
        yield put(VendorActions.postblogadminFailure());
    }
}

export function* getblogRequest(api, action) {
    try {
        const { companyId } = action;
        const response = yield api.getGetBlog('company', companyId);
        if (response.ok) {
            yield put(VendorActions.getblogSuccess({ id: companyId, blog: response.data }));
        } else {
            yield put(VendorActions.getblogFailure());
        }
    } catch (e) {
        yield put(VendorActions.getblogFailure());
    }
}

export function* saveblogRequest(api, action) {
    try {
        const { data, route, after, blog } = action.payload;

        let payload = { ...data };
        if (blog) {
            const res = yield api.postCreateBlog(blog);
            if (res.ok) {
                payload = {
                    ...data,
                    blog: {
                        ...data.blog,
                        id: res.data?.blog?.id,
                        entity_id: res.data?.entity?.id,
                        group_id: res.data?.entity?.group_id,
                    },
                };
            } else {
                toast.error(`Oops, Your action cannot be completed because you don't have permission to create a blog`, {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                });
                yield put(VendorActions.saveblogFailure());
                return;
            }
        }

        const response = yield api.postSaveBlog(payload);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            toast.success(`Your action was completed successfully.`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.saveblogSuccess());
            yield put(AppActions.modalRequest({ type: '' }));
        } else {
            toast.error(`Oops, Your action cannot be completed. Please retry later!`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.saveblogFailure());
        }
    } catch (e) {
        yield put(VendorActions.saveblogFailure());
    }
}

export function* getblogpostsRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(payload);

        const response = yield api.getBlogPosts(options);
        if (response.ok) {
            yield put(VendorActions.getblogpostsSuccess(response.data));
        } else {
            yield put(VendorActions.getblogpostsFailure());
        }
    } catch (e) {
        yield put(VendorActions.getblogpostsFailure());
    }
}

export function* saveblogpostRequest(api, action) {
    const { data, route, callback, after } = action.payload;
    try {
        const response = yield api.postSaveBlogPost(data);

        if (response.ok) {
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
            yield put(VendorActions.saveblogpostSuccess());

            if (after?.businessId) {
                const allBusiness = yield select((state) => state.company.business);
                const business = clone(find(propEq('id', after?.businessId), allBusiness));
                const idx1 = findIndex(propEq('id', response.data.post.id), business?.vrs?.blog?.data?.posts.data);
                if (idx1 > -1) {
                    business.vrs.blog.data.posts.data[idx1] = response.data.post;
                }
                const idx2 = findIndex(propEq('id', response.data.post.id), business?.vrs?.blog?.data?.drafts.data);
                if (idx2 > -1) {
                    business.vrs.blog.data.drafts.data[idx2] = response.data.post;
                }
                if (idx1 === -1 && idx2 === -1) {
                    if (data.post.status === 0) {
                        business.vrs.blog.data.posts.data = [response.data.post, ...business.vrs.blog.data.posts.data];
                    } else if (data.post.status === 2) {
                        business.vrs.blog.data.drafts.data = [response.data.post, ...business.vrs.blog.data.drafts.data];
                    }
                }
                yield put(CompanyActions.getbusinessSuccess([business]));
            }
            if (after?.userId) {
                const allCommunityBlogs = yield select((state) => state.community.communityBlogs);
                const blogs = clone(find(propEq('user_id', after?.userId), allCommunityBlogs));
                const idx1 = findIndex(propEq('id', response.data.post.id), blogs?.posts?.data ?? []);
                if (idx1 > -1) {
                    blogs.posts.data[idx1] = response.data.post;
                    yield put(CommunityActions.getcommunityblogSuccess(blogs));
                }
                const idx2 = findIndex(propEq('id', response.data.post.id), blogs?.drafts?.data ?? []);
                if (idx2 > -1) {
                    blogs.drafts.data[idx2] = response.data.post;
                    yield put(CommunityActions.getcommunityblogSuccess(blogs));
                }
            }

            // yield put(CompanyActions.getblogcategoriesRequest())
            toast.success('Blog was successfully saved.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            toast.error(response.data?.message || `Your action cannot be completed!\n Please try it later!`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.saveblogpostFailure());
        }
    } catch (e) {
        toast.error(`Your action cannot be completed!\n Please try it later!`, {
            position: toast.POSITION.TOP_RIGHT,
            pauseOnFocusLoss: false,
            hideProgressBar: true,
        });
        yield put(VendorActions.saveblogpostFailure());
    }
}

export function* deleteblogpostRequest(api, action) {
    const { data, route, after, callback } = action.payload;
    try {
        const response = yield api.postDeleteBlogPost(data);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
            yield put(VendorActions.deleteblogpostSuccess());
            toast.success('Blog was successfully deleted', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            yield put(VendorActions.deleteblogpostFailure());
        }
    } catch (e) {
        yield put(VendorActions.deleteblogpostFailure());
    }
}

export function* postadduserRequest(api, action) {
    try {
        const { data, after, route, callback } = action.payload;
        const options = decamelize(data);
        const response = yield api.postAddUser(options);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
            yield put(VendorActions.postadduserSuccess(response.data));
        } else {
            yield put(VendorActions.postadduserFailure());
        }
    } catch (e) {
        yield put(VendorActions.postadduserFailure());
    }
}

export function* postremoveuserRequest(api, action) {
    try {
        const { data, after, route, callback } = action.payload;
        const options = decamelize(data);
        const response = yield api.postRemoveUser(options);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
            yield put(VendorActions.postremoveuserSuccess(response.data));
        } else {
            yield put(VendorActions.postremoveuserFailure());
        }
    } catch (e) {
        yield put(VendorActions.postremoveuserFailure());
    }
}

export function* postsaveproductRequest(api, action) {
    try {
        const { payload, callback, after } = action;
        const response = yield api.postSaveProduct(payload);

        if (response.ok) {
            yield put(VendorActions.postsaveproductSuccess(response.data));
            toast.success('Product was successfully saved', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield callback();
            if (after) yield put({ ...after });
        } else {
            yield put(VendorActions.postsaveproductFailure());
        }
    } catch (e) {
        yield put(VendorActions.postsaveproductFailure());
    }
}

export function* postsavecontentRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postSaveContent(payload);

        if (response.ok) {
            if (after?.businessId) {
                const businesses = yield select((state) => state.company.business);
                const business = clone(find(propEq('id', after?.businessId), businesses));
                if (business) {
                    business.vrs.products = business.vrs.products.filter((p) => p.id !== payload.content.id);
                    yield put(CompanyActions.getbusinessSuccess([business]));
                }
            }
            yield put(VendorActions.postsavecontentSuccess());
            toast.success(`${payload.content?.deleted_at ? 'Product was successfully deleted.' : 'Product was successfully saved'}`, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            if (after?.callback) {
                yield after.callback();
            }
        } else {
            yield put(VendorActions.postsavecontentFailure());
        }
    } catch (e) {
        yield put(VendorActions.postsavecontentFailure());
    }
}

export function* postsavevrmediaRequest(api, action) {
    try {
        const { payload, callback } = action;

        const res = yield api.getCommunityS3Info();
        const resKey = yield api.getGenerateHash();
        if (res.ok) {
            const formData = new FormData();
            for (const key in res.data.inputs) {
                formData.append(key, res.data.inputs[key]);
            }
            formData.set('Content-Type', payload.type);
            formData.append('key', `content/${resKey.data}/${payload.name}`);
            formData.append('file', payload);
            const response = yield api.postUploadfile(res.data.url, formData);

            if (response.ok) {
                const data = xmljs.xml2json(response.data, { compact: true, ignoreDeclaration: true });
                const url = JSON.parse(data).PostResponse.Location._text;
                if (callback) yield callback(url);
                yield put(VendorActions.postsavevrmediaSuccess());
            }
        } else {
            yield put(VendorActions.postsavevrmediaFailure());
        }
    } catch (e) {
        yield put(VendorActions.postsavevrmediaFailure());
    }
}

export function* postsaveproductmediaRequest(api, action) {
    try {
        const { payload, media, after } = action;

        if (payload.type === 'video') {
            const res = yield api.getCommunityS3Info();
            const resKey = yield api.getGenerateHash();
            if (res.ok) {
                const formData = new FormData();
                for (const key in res.data.inputs) {
                    formData.append(key, res.data.inputs[key]);
                }
                formData.set('Content-Type', payload.video.type);
                formData.append('key', `content/${resKey.data}/${payload.video.name}`);
                formData.append('file', payload.video);
                const _response = yield api.postUploadfile(res.data.url, formData);

                if (_response.ok) {
                    const data = xmljs.xml2json(_response.data, { compact: true, ignoreDeclaration: true });
                    const url = JSON.parse(data).PostResponse.Location._text;
                    delete payload.video;
                    payload.video_url = url;

                    const response = yield api.postSaveProductMedia(payload);
                    if (response.ok) {
                        if (media) yield api.deleteProductMedia(media);
                        if (after) yield put({ ...after });
                        const business = yield select((state) => state.company.business);
                        for (let item of business) {
                            const index = findIndex((e) => e.id === response.data.parent_id, item.vrs?.products || []);
                            if (index > -1) {
                                const temp = clone(item);
                                temp.vrs.products[index][`${payload.type}s`] = [response.data, ...temp.vrs.products[index][`${payload.type}s`]];
                                temp.vrs.products[index][`${payload.type}s`].filter((e) => e.id !== media?.id);
                                yield put(CompanyActions.getbusinessSuccess([temp]));
                            }
                        }
                        yield put(VendorActions.postsaveproductmediaSuccess(response.data));
                    } else {
                        yield put(VendorActions.postsaveproductmediaFailure());
                    }
                }
            }
        } else {
            const response = yield api.postSaveProductMedia(payload);
            if (response.ok) {
                if (media) yield api.deleteProductMedia(media);
                const business = yield select((state) => state.company.business);
                if (after) yield put({ ...after });
                for (let item of business) {
                    const index = findIndex((e) => e.id === response.data.parent_id, item.vrs?.products || []);
                    if (index > -1) {
                        const temp = clone(item);
                        temp.vrs.products[index][`${payload.type}s`] = [response.data, ...temp.vrs.products[index][`${payload.type}s`]];
                        temp.vrs.products[index][`${payload.type}s`].filter((e) => e.id !== media?.id);
                        yield put(CompanyActions.getbusinessSuccess([temp]));
                    }
                }
                yield put(VendorActions.postsaveproductmediaSuccess(response.data));
            } else {
                yield put(VendorActions.postsaveproductmediaFailure());
            }
        }
    } catch (e) {
        yield put(VendorActions.postsaveproductmediaFailure());
    }
}

export function* postdeleteproductmediaRequest(api, action) {
    try {
        const { data, after, route, callback } = action.payload;
        const response = yield api.deleteProductMedia(data);
        if (response.ok) {
            if (after) yield put({ ...after });
            if (route) yield call(NavigationService.push, route);
            if (callback) yield callback();
            yield put(VendorActions.postdeleteproductmediaSuccess());
        } else {
            yield put(VendorActions.postdeleteproductmediaFailure());
        }
    } catch (e) {
        yield put(VendorActions.postdeleteproductmediaFailure());
    }
}

export function* getsuperadminbusinessRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload));
        const response = yield api.getSuperAdminBusiness(options);

        if (response.ok) {
            yield put(VendorActions.getsuperadminbusinessSuccess(response.data));
        } else {
            yield put(VendorActions.getsuperadminbusinessFailure());
        }
    } catch (e) {
        yield put(VendorActions.getsuperadminbusinessFailure());
    }
}

export function* getsuperadminusersRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, decamelize(payload)), {
            arrayFormat: 'bracket',
        });
        const response = yield api.getSuperAdminUserContent(options);
        if (response.ok) {
            yield put(VendorActions.getsuperadminusersSuccess(response.data.users));
        } else {
            yield put(VendorActions.getsuperadminusersFailure());
        }
    } catch (e) {
        yield put(VendorActions.getsuperadminusersFailure());
    }
}

export function* getsuperadmincontentRequest(api, action) {
    try {
        const { payload } = action;
        const options = queryString.stringify(pickBy(isNotNull, payload), { arrayFormat: 'bracket' });
        const response = yield api.getSuperAdminContent(options);
        if (response.ok) {
            yield put(VendorActions.getsuperadmincontentSuccess(response.data.content));
        } else {
            yield put(VendorActions.getsuperadmincontentFailure());
        }
    } catch (e) {
        yield put(VendorActions.getsuperadmincontentFailure());
    }
}

export function* postsuperadminbanduserRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postSuperAdminBandUserContent(payload);
        if (response.ok) {
            yield put(VendorActions.postsuperadminbanduserSuccess());
            if (after) yield put({ ...after });
        } else {
            yield put(VendorActions.postsuperadminbanduserFailure());
        }
    } catch (e) {
        yield put(VendorActions.postsuperadminbanduserFailure());
    }
}

export function* postsuperadminrestoreuserRequest(api, action) {
    try {
        const { payload, after } = action;
        const response = yield api.postSuperAdminRestoreUserContent(payload);
        if (response.ok) {
            yield put(VendorActions.postsuperadminrestoreuserSuccess());
            if (after) yield put({ ...after });
        } else {
            yield put(VendorActions.postsuperadminrestoreuserFailure());
        }
    } catch (e) {
        yield put(VendorActions.postsuperadminrestoreuserFailure());
    }
}

export function* postvendorconnectRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postConnection(payload);
        if (response.ok) {
            toast.success('Invite has been sent.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postvendorconnectSuccess());
        } else {
            toast.error(response.data.message, {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            yield put(VendorActions.postvendorconnectFailure());
        }
    } catch (e) {
        toast.error(e, {
            position: toast.POSITION.TOP_RIGHT,
            pauseOnFocusLoss: false,
            hideProgressBar: true,
        });
        yield put(VendorActions.postvendorconnectFailure());
    }
}

export function* postcategorysaveRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postCategorySave(payload);

        if (response.ok) {
            yield put(VendorActions.postcategorysaveSuccess(response.data));
        } else {
            yield put(VendorActions.postcategorysaveFailure());
        }
    } catch (e) {
        yield put(VendorActions.postcategorysaveFailure());
    }
}

export function* postcategorydeleteRequest(api, action) {
    try {
        const { payload, callback } = action;
        const response = yield api.postCategoryDelete(payload);

        if (response.ok) {
            yield put(VendorActions.postcategorydeleteSuccess(response.data));
            if (callback)
                yield put(
                    VendorActions.getvendorcategoriesRequest({
                        page: 1,
                        per_page: 10,
                        parent_id: 3,
                        deleted: callback === 'all' ? null : callback === 'active' ? 0 : 1,
                    })
                );
        } else {
            yield put(VendorActions.postcategorydeleteFailure());
        }
    } catch (e) {
        yield put(VendorActions.postcategorydeleteFailure());
    }
}

export function* postcategoryrestoreRequest(api, action) {
    try {
        const { payload, callback } = action;
        const response = yield api.postCategoryRestore(payload);

        if (response.ok) {
            yield put(VendorActions.postcategoryrestoreSuccess(response.data));
            if (callback)
                yield put(
                    VendorActions.getvendorcategoriesRequest({
                        page: 1,
                        per_page: 10,
                        parent_id: 3,
                        deleted: callback === 'all' ? null : callback === 'active' ? 0 : 1,
                    })
                );
        } else {
            yield put(VendorActions.postcategoryrestoreFailure());
        }
    } catch (e) {
        yield put(VendorActions.postcategoryrestoreFailure());
    }
}

export function* postvendorratingRequest(api, action) {
    try {
        const { payload } = action;
        const response = yield api.postVendorRating(payload);

        if (response.ok) {
            yield put(VendorActions.postvendorratingSuccess(response.data));
        } else {
            yield put(VendorActions.postvendorratingFailure());
        }
    } catch (e) {
        yield put(VendorActions.postvendorratingFailure());
    }
}
